import { Button, ButtonInline } from '@remote-com/norma/core/button';
import DollarIcon from '@remote-com/norma/icons/dollar-sign.svg';
import FileIcon from '@remote-com/norma/icons/file-text.svg';
import PayslipIcon from '@remote-com/norma/icons/payslips-deep.svg';
import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { Section as SectionComponent } from './ComparisonTableSlice';
import CircleImage from './assets/circle.png';
import EurIcon from './assets/eur.svg';

import {
  OverviewLabel,
  OverviewTitleComponent,
  OverviewDescriptionComponent,
} from '@/src/components/OverviewSection';
import { transformProperties } from '@/src/domains/prismic/helpers';
import { tabletBreakpoint, fromTabletBreakpoint } from '@/src/domains/shared/breakpoints';

export const RowStyle = css`
  padding-top: 28px;
  padding-bottom: 30px;
  text-align: left;
  border-bottom: 1px solid rgba(82, 95, 127, 0.3);

  ${tabletBreakpoint(css`
    width: 80%;
    margin: auto;
    border: 1px solid rgba(82, 95, 127, 0.3);
    text-align: center;
  `)}
`;

export const HeadingStyle = css`
  ${({ theme }) => theme.typography.bodySMBold}
  color: #778CA3;
  text-transform: uppercase;
  align-items: center;
  display: flex;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export const OverviewSectionHeader = styled.div`
  text-align: center;
  ${fluidProp(['padding-top'], '60px', '160px')}
  ${fluidProp(['padding-bottom'], '60px', '100px')}
`;

export const Section = styled(SectionComponent)`
  background-image: var(--colors-zircon);
`;

export const Description = styled(OverviewDescriptionComponent)`
  color: var(--colors-bayoux);
`;

export const FirstColumn = styled.div`
  width: 30%;
  ${RowStyle}

  ${fromTabletBreakpoint(css`
    padding-left: 15px;
  `)}
`;

export const SecondColumn = styled.div`
  width: 60%;
  ${RowStyle}
`;

export const ThirdColumn = styled.div`
  ${RowStyle}
  && {
    width: 30%;
    text-align: center;
    background: #f0f5ff;
    border-color: rgba(82, 95, 127, 0.1);
    font-weight: 500;
    color: var(--colors-darkBlue);

    ${tabletBreakpoint(css`
      width: 80%;
      margin: auto;
      border: 1px solid rgba(82, 95, 127, 0.3);
    `)}
  }
`;

export const PricingTable = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 66px;
`;

export const FirstColumnTitle = styled(FirstColumn)`
  ${HeadingStyle}
`;

export const SecondColumnTitle = styled(SecondColumn)`
  ${HeadingStyle}
`;

export const ThirdColumnTitle = styled(ThirdColumn)`
  && {
    ${HeadingStyle}
    border-top-left-radius: 10px;
    border-top-right-radius: 10px;
    justify-content: center;
  }
`;

export const PricingRow = styled.div`
  display: flex;
  justify-content: space-between;
  color: var(--colors-darkBlue);
  ${({ theme }) => theme.typography.body}

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  &:last-child {
    justify-content: flex-end;

    ${ThirdColumn} {
      border: none;
      width: 25%;
      background-color: var(--colors-irisBlue);
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
      ${({ theme }) => theme.typography.scale4}
      ${fluidProp('font-size', '26px', '32px')}
      color: var(--colors-blank);

      ${tabletBreakpoint(css`
        width: 80%;
        margin: auto;
        text-align: center;
      `)}
    }
  }

  ${tabletBreakpoint(css`
    flex-direction: column;
  `)}

  &:nth-child(3) {
    ${FirstColumn} {
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
    }
  }
`;

export const IconWrapper = styled.div`
  margin-right: 11px;
  display: flex;

  svg {
    width: 85%;
  }
`;

export const TotalPrice = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: center;

  svg {
    padding-top: 7px;
    margin-left: 6px;
  }
`;

export const TotalDescription = styled.div`
  color: var(--colors-turquoise);
  ${({ theme }) => theme.typography.bodySMBold}
  text-transform: uppercase;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

export const CtaWrapper = styled.div`
  text-align: center;
`;

export const CurrencyWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  ${tabletBreakpoint(css`
    width: 90%;
  `)}
`;

export const CurrencyButtons = styled.div`
  display: flex;
  width: 25%;
  justify-content: center;
`;

export const ImageWrapper = styled.div`
  position: absolute;
  top: -7%;

  ${tabletBreakpoint(css`
    display: none;
  `)}
`;

export const DollarWrapper = styled.div`
  display: flex;

  svg {
    max-width: 15px;
  }
`;

export const HeadingMobileTitle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--colors-irisBlue);

  ${fromTabletBreakpoint(css`
    display: none;
  `)}

  ${IconWrapper} {
    display: flex;
    align-items: center;
    margin-right: 0;
  }
`;

export const CurrencyButton = styled(ButtonInline)`
  && {
    text-transform: uppercase;
    margin-left: 7px;
    margin-right: 7px;
    opacity: 0.3;
    text-decoration: none;

    ${({ $active }) => {
      switch ($active) {
        case true:
          return css`
            color: var(--colors-irisBlue);
            opacity: 1;
          `;
        case false:
        default:
          return css`
            color: var(--colors-darkBlue);
          `;
      }
    }}
  }
`;

export function EntityExampleSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const { ctaLabel, ctaLink, label, title, description, totalEur, totalUsd } = primary;

  const firstColumnTitle = 'Type of Cost';
  const secondColumnTitle = 'Description';
  const thirdColumnTitle = 'Approximate Cost';

  const [currencyValue, setCurrencyValue] = useState('eur');

  return (
    <Section>
      <AnimatedSection ComponentName={OverviewSectionHeader}>
        <ContentContainer>
          {label && (
            <OverviewLabel>
              <small>{label}</small>
            </OverviewLabel>
          )}
          {title && <RichText render={title} Component={OverviewTitleComponent} />}
          {description && <RichText render={description} Component={Description} />}

          <PricingTable>
            <CurrencyWrapper>
              <CurrencyButtons>
                <CurrencyButton
                  onClick={() => setCurrencyValue('usd')}
                  $active={currencyValue === 'usd'}
                >
                  $ USD
                </CurrencyButton>
                <CurrencyButton
                  onClick={() => setCurrencyValue('eur')}
                  $active={currencyValue === 'eur'}
                >
                  € EUR
                </CurrencyButton>
              </CurrencyButtons>
            </CurrencyWrapper>
            <PricingRow>
              <FirstColumnTitle>
                <IconWrapper>
                  <DollarIcon />
                </IconWrapper>
                {firstColumnTitle}
              </FirstColumnTitle>
              <SecondColumnTitle>
                <IconWrapper>
                  <FileIcon />
                </IconWrapper>
                {secondColumnTitle}
              </SecondColumnTitle>
              <ThirdColumnTitle>
                <IconWrapper>
                  <DollarIcon />
                </IconWrapper>
                {thirdColumnTitle}
              </ThirdColumnTitle>
            </PricingRow>
            {fields.map(({ firstColumn, secondColumn, thirdColumn, fourthColumn }, index) => (
              <PricingRow key={index}>
                {firstColumn && (
                  <FirstColumn>
                    <HeadingMobileTitle>
                      <IconWrapper>
                        <DollarIcon />
                      </IconWrapper>
                      {firstColumnTitle}
                    </HeadingMobileTitle>
                    <RichText render={firstColumn} />
                  </FirstColumn>
                )}
                {secondColumn && (
                  <SecondColumn>
                    <HeadingMobileTitle>
                      <IconWrapper>
                        <FileIcon />
                      </IconWrapper>
                      {secondColumnTitle}
                    </HeadingMobileTitle>
                    <RichText render={secondColumn} />
                  </SecondColumn>
                )}
                {thirdColumn && fourthColumn && (
                  <ThirdColumn>
                    <HeadingMobileTitle>
                      <IconWrapper>
                        <DollarIcon />
                      </IconWrapper>
                      {thirdColumnTitle}
                    </HeadingMobileTitle>
                    <RichText render={currencyValue === 'eur' ? fourthColumn : thirdColumn} />
                  </ThirdColumn>
                )}
              </PricingRow>
            ))}
            <PricingRow>
              <ThirdColumn>
                <TotalDescription>
                  <IconWrapper>
                    <PayslipIcon />
                  </IconWrapper>
                  Total cost
                </TotalDescription>
                {currencyValue === 'usd' ? (
                  <TotalPrice>
                    {totalUsd}
                    <DollarWrapper>
                      <DollarIcon />
                    </DollarWrapper>
                  </TotalPrice>
                ) : (
                  <TotalPrice>
                    {totalEur}
                    <EurIcon />
                  </TotalPrice>
                )}
              </ThirdColumn>
            </PricingRow>
          </PricingTable>
        </ContentContainer>
      </AnimatedSection>
      {ctaLink && ctaLabel && (
        <CtaWrapper>
          <Button href={ctaLink.url} target={ctaLink.target}>
            {ctaLabel}
          </Button>
        </CtaWrapper>
      )}
      <ImageWrapper>
        <Image src={CircleImage} quality="100" alt="" />
      </ImageWrapper>
    </Section>
  );
}
